import type { Account, Workspace } from '@deskworks/shared-types';
import { useQueries } from '@tanstack/react-query';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useMe, useWorkspace } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/** Compact workspace dropdown for page headers; `onSelect` decides where the chosen workspace leads. */
export function WorkspacePicker({
  workspaceId,
  onSelect,
  className,
}: {
  workspaceId: string;
  onSelect: (workspaceId: string) => void;
  className?: string;
}) {
  const me = useMe();
  const current = useWorkspace(workspaceId);
  const accounts: Account[] = me.data?.accounts ?? [];
  const wsQueries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: qk.accountWorkspaces(a.id),
      queryFn: () => api.accounts.workspaces(a.id),
    })),
  });
  const byAccount: { account: Account; workspaces: Workspace[] }[] = accounts
    .map((a, i) => ({ account: a, workspaces: wsQueries[i]?.data ?? [] }))
    .filter((g) => g.workspaces.length > 0);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className={cn('max-w-56', className)} title={t('Change workspace')}>
          <span className="truncate">{current.data?.name ?? t('Workspace')}</span>
          <ChevronsUpDown className="text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {byAccount.map(({ account, workspaces }, idx) => (
          <div key={account.id}>
            {idx > 0 && <DropdownMenuSeparator />}
            {accounts.length > 1 && <DropdownMenuLabel>{account.name}</DropdownMenuLabel>}
            {workspaces.map((w) => (
              <DropdownMenuItem key={w.id} onSelect={() => w.id !== workspaceId && onSelect(w.id)}>
                <span className="flex-1 truncate">{w.name}</span>
                {w.id === workspaceId && <Check className="text-primary" />}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
